import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { servicesCards, images, companyInfo } from '../data/mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle, Search, ClipboardList, Stethoscope, Phone, X, ZoomIn, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JoinStudyPage = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', age: '', condition: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const conditions = ['Diabetes', 'Heart Disease', 'Cancer', 'Respiratory', 'Neurological', 'Autoimmune', 'Mental Health', 'Other'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/join-study`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          age: parseInt(formData.age, 10)
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit registration');
      }
      
      setSubmitted(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', age: '', condition: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(err.message || 'Failed to submit registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero - Compact */}
      <section className="pt-6 pb-10 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={images.medical} alt="Join Study" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/80 to-[#0a1628]/60" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Join a <span className="text-[#87CEEB]">Study</span></h1>
          <p className="text-base text-white/80 max-w-2xl">Find a study for your condition and contribute to medical advancements.</p>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section className="bg-[#1877F2] py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <span className="text-white font-medium">Questions? Call us:</span>
            <a href={`tel:${companyInfo.contact.phone}`} className="flex items-center space-x-2 bg-white text-[#1877F2] px-4 py-1.5 rounded-full font-bold text-sm hover:bg-[#E6F4FA] transition-all" data-testid="join-study-call-btn">
              <Phone size={16} />
              <span>{companyInfo.contact.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Form - Compact */}
      <section className="py-8 bg-[#F8FBFF]">
        <div className="max-w-xl mx-auto px-4">
          <h2 className="text-xl font-bold text-center text-gray-900 mb-4">Register Your <span className="text-[#1877F2]">Interest</span></h2>
          <Card className="border-0 shadow-xl rounded-xl">
            <CardContent className="p-5">
              {submitted ? (
                <div className="flex flex-col items-center py-6 text-center" data-testid="join-success-message">
                  <CheckCircle className="w-12 h-12 text-green-500 mb-2" />
                  <h3 className="text-lg font-semibold mb-1">Registration Submitted!</h3>
                  <p className="text-gray-600 text-sm">We'll contact you soon at the email you provided.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" data-testid="join-study-form">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
                      {error}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="First Name *" required value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} className="h-10 rounded-lg text-sm" data-testid="join-firstname-input" disabled={loading} />
                    <Input placeholder="Last Name *" required value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} className="h-10 rounded-lg text-sm" data-testid="join-lastname-input" disabled={loading} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input type="email" placeholder="Email *" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="h-10 rounded-lg text-sm" data-testid="join-email-input" disabled={loading} />
                    <Input placeholder="Phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="h-10 rounded-lg text-sm" data-testid="join-phone-input" disabled={loading} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input type="number" placeholder="Age *" required min="18" value={formData.age} onChange={(e) => setFormData({...formData, age: e.target.value})} className="h-10 rounded-lg text-sm" data-testid="join-age-input" disabled={loading} />
                    <Select value={formData.condition} onValueChange={(v) => setFormData({...formData, condition: v})} disabled={loading}>
                      <SelectTrigger className="h-10 rounded-lg text-sm" data-testid="join-condition-select"><SelectValue placeholder="Condition *" /></SelectTrigger>
                      <SelectContent>
                        {conditions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea placeholder="Additional information (optional)..." rows={2} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} className="rounded-lg text-sm" data-testid="join-message-input" disabled={loading} />
                  <Button type="submit" className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white py-4 rounded-lg font-semibold" data-testid="join-submit-btn" disabled={loading || !formData.condition}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
                      </>
                    ) : (
                      'Submit Registration'
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Stages of Clinical Research - Clickable Image */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">Phases of <span className="text-[#1877F2]">Clinical Trials</span></h3>
            <p className="text-gray-600 text-sm">Clinical trials progress through distinct phases to ensure safety and effectiveness.</p>
            <p className="text-[#1877F2] text-xs mt-1">Click image to enlarge</p>
          </div>
          <div 
            className="relative cursor-pointer group"
            onClick={() => setImageModalOpen(true)}
            data-testid="stages-image-clickable"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_hyde-park-clinic/artifacts/c4gp6ccc_stages-of-clinical-research.webp" 
              alt="Stages of Clinical Research"
              className="w-full h-48 object-contain rounded-xl transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="bg-[#1877F2]/80 text-white p-3 rounded-full">
                <ZoomIn className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Modal/Lightbox */}
      {imageModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setImageModalOpen(false)}
          data-testid="image-modal-overlay"
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setImageModalOpen(false)}
            data-testid="image-modal-close"
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src="https://customer-assets.emergentagent.com/job_hyde-park-clinic/artifacts/c4gp6ccc_stages-of-clinical-research.webp" 
            alt="Stages of Clinical Research - Enlarged"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

export default JoinStudyPage;
